import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { WorkbenchCard } from '@mybusinessmate/schema';
import { TASK_TYPE_META } from '../../lib/task-display';
import { toasts } from '../../lib/toast-presets';
import { useSession } from '../../lib/session';
import { staffMeQuery } from '../../lib/workspace-api';
import {
  matchesFilters,
  readView,
  saveView,
  type WorkbenchFilters,
  type WorkbenchView,
} from '../../lib/workbench-display';
import { workbenchQuery } from '../../lib/workbench-api';
import { Button } from '../ui/button';
import { EmptyState } from '../ui/empty-state';
import { SegmentedControl } from '../ui/segmented-control';
import { PageSkeleton } from '../ui/skeleton';
import { LoadFailure, NoResults } from '../ui/state-views';
import { FilterSelectPill, PillButton, ToolbarSearch } from '../ui/toolbar-controls';
import { useToast } from '../ui/toast';
import { Board } from './board';
import { CapacityGauge } from './capacity-gauge';
import { QueueList } from './queue-list';
import { QuickPeek } from './quick-peek';

const NO_FILTERS: WorkbenchFilters = { q: '', business: '', type: '' };

/**
 * "My work" (Figma A05.7 to A05.7d): a specialist's or lead's own board, and the same cards as a daily queue. The view
 * they used last is remembered per person. The Calendar tab of the design is left out until it has a screen.
 */
export function MyWorkPage() {
  const toast = useToast();
  const { data: session } = useSession();
  const userId = session?.user.id;
  const me = useQuery(staffMeQuery);
  const board = useQuery(workbenchQuery);

  const [view, setView] = useState<WorkbenchView>(() => readView(userId));
  useEffect(() => setView(readView(userId)), [userId]);
  const [filters, setFilters] = useState<WorkbenchFilters>(NO_FILTERS);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const items = useMemo(() => board.data?.items ?? [], [board.data]);
  const visible = useMemo(() => items.filter((card) => matchesFilters(card, filters)), [items, filters]);
  const businesses = useMemo(
    () => [...new Set(items.map((card) => card.businessName).filter((name): name is string => !!name))].sort(),
    [items],
  );
  const types = useMemo(() => [...new Set(items.map((card) => card.taskType))], [items]);
  const selected: WorkbenchCard | null = items.find((card) => card.taskId === selectedId) ?? null;

  const subtitle = [session?.user.name, me.data?.title, me.data?.department].filter(Boolean).join(' · ');

  const pickView = (next: WorkbenchView) => {
    setView(next);
    saveView(userId, next);
  };

  if (board.isPending) return <PageSkeleton rows={5} label="Loading your work" />;
  if (board.isError) return <LoadFailure error={board.error} noun="workbench" onRetry={() => void board.refetch()} />;

  const { capacity } = board.data;
  const open = (card: WorkbenchCard) => setSelectedId(card.taskId);

  return (
    <div className="flex flex-col gap-(--mbm-space-5)">
      <header className="flex flex-wrap items-center justify-between gap-(--mbm-space-4) pt-(--mbm-space-1)">
        <div className="flex flex-col gap-(--mbm-space-1-5)">
          <h1 className="text-heading-page text-text-strong">My work</h1>
          {subtitle ? <p className="text-body-compact text-text-secondary">{subtitle}</p> : null}
        </div>
        <div className="flex items-center gap-(--mbm-space-2-5)">
          <CapacityGauge active={capacity.active} max={capacity.max} />
          {/* TODO(api): a time range for the board; the design shows "This week" and nothing else about it. */}
          <PillButton
            label="This week"
            size="lg"
            onClick={() => toast(toasts.comingSoon('Picking a time range for your work is coming soon.'))}
          />
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-(--mbm-space-2-5)">
        <ToolbarSearch
          filled
          size="lg"
          value={filters.q}
          onChange={(q) => setFilters((f) => ({ ...f, q }))}
          placeholder="Search tasks or clients"
          aria-label="Search your tasks"
          className="w-(--mbm-layout-toolbar-search-mid-width)"
        />
        <SegmentedControl
          label="View"
          shape="pill"
          size="lg"
          value={view}
          onChange={pickView}
          options={[
            { value: 'board', label: 'Board' },
            { value: 'list', label: 'List' },
          ]}
          className="[&>legend]:sr-only"
        />
        <div className="ml-auto flex items-center gap-(--mbm-space-2-5)">
          <FilterSelectPill
            size="lg"
            showAll
            label="Business"
            value={filters.business}
            onChange={(business) => setFilters((f) => ({ ...f, business }))}
            options={businesses.map((name) => ({ value: name, label: name }))}
          />
          <FilterSelectPill
            size="lg"
            showAll
            label="Type"
            value={filters.type}
            onChange={(type) => setFilters((f) => ({ ...f, type }))}
            options={types.map((type) => ({ value: type, label: TASK_TYPE_META[type].short }))}
          />
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-(--mbm-component-radius-widget) border border-border-line bg-surface-card">
          <EmptyState
            title="No tasks assigned yet"
            description="When your lead assigns work, it lands in To do here, soonest deadline first. Nothing to do until then."
            action={
              // TODO(api): chat with your lead (the Messages area is not built).
              <Button
                variant="secondary"
                size="dialog"
                className="border border-border-input"
                onClick={() => toast(toasts.comingSoon('Messaging your lead is coming soon.'))}
              >
                Message your lead
              </Button>
            }
          />
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-(--mbm-component-radius-widget) border border-border-line bg-surface-card">
          <NoResults
            query={filters.q.trim() || undefined}
            description="Try fewer words, or clear the filters."
            onClear={() => setFilters(NO_FILTERS)}
          />
        </div>
      ) : view === 'board' ? (
        <Board cards={visible} viewerId={userId} selectedId={selectedId} onOpen={open} />
      ) : (
        <QueueList cards={visible} viewerId={userId} selectedId={selectedId} onOpen={open} />
      )}

      <QuickPeek card={selected} onClose={() => setSelectedId(null)} />
    </div>
  );
}
